/**
 * Update Organization Hook
 * 
 * Hook to update organization details using GraphQL mutation via Axios.
 * Follows the pattern from useFetchUserData.ts
 * 
 * Uses: React Query + Axios
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/dataProvider/axios";

// GraphQL mutation string
const UPDATE_ORG_MUTATION = `
  mutation UpdateOrg($input: UpdateOrganizationInput!) {
    updateOrganization(input: $input) {
      id
      orgName
      orgType
      instanceUrl
    }
  }
`;

/**
 * API Input Types
 */
interface UpdateOrganizationInput {
  id: string;
  orgName?: string;
  orgType?: string;
}

/**
 * API Response Types
 */
interface UpdatedOrganization {
  id: string;
  orgName: string;
  orgType: string;
  instanceUrl: string;
}

interface UpdateOrgResponse {
  updateOrganization: UpdatedOrganization;
}

/**
 * Hook to update organization details
 * 
 * @returns Mutation object with mutate, isLoading, error, etc.
 * 
 * @example
 * const updateOrg = useUpdateOrg();
 * 
 * updateOrg.mutate({
 *   id: "org-id",
 *   orgName: "New Name"
 * }, {
 *   onSuccess: (data) => {
 *     console.log("Updated:", data);
 *   }
 * });
 */
export function useUpdateOrg() {
  const queryClient = useQueryClient();

  return useMutation<UpdatedOrganization, Error, UpdateOrganizationInput>({
    mutationFn: async (input: UpdateOrganizationInput) => {
      console.log("🔄 Updating organization:", input);

      // GraphQL request via Axios (POST to /graphql)
      const response = await axiosInstance.post<UpdateOrgResponse>('/graphql', {
        query: UPDATE_ORG_MUTATION,
        variables: { input },
      });

      console.log("✅ Organization updated:", response.data);
      
      // Extract the updated organization from response
      // Axios interceptor already unwrapped { data: {...} }
      return response.data.updateOrganization;
    },
    onSuccess: () => {
      // Invalidate and refetch user data to get updated organization list
      queryClient.invalidateQueries({ queryKey: ["user-data"] });
      console.log("✅ Invalidated user-data query, will refetch");
    },
  });
}

